
// Google Analytics Consent Management
declare global {
  interface Window {
    gtag: (command: string, ...args: any[]) => void;
    dataLayer: any[];
  }
}

export type ConsentLevel = 'granted' | 'denied';

export interface ConsentSettings {
  analytics_storage: ConsentLevel;
  ad_storage: ConsentLevel;
  functionality_storage: ConsentLevel;
  personalization_storage: ConsentLevel;
}

// Cookie consent management
export const CONSENT_COOKIE = 'kodibuild_consent';
export const CONSENT_EXPIRY_DAYS = 365;

export const getStoredConsent = (): ConsentSettings | null => {
  try {
    const consent = localStorage.getItem(CONSENT_COOKIE);
    return consent ? JSON.parse(consent) : null;
  } catch {
    return null;
  }
};

export const storeConsent = (consent: ConsentSettings): void => {
  try {
    localStorage.setItem(CONSENT_COOKIE, JSON.stringify(consent));
    
    // Set expiry date
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);
    localStorage.setItem(`${CONSENT_COOKIE}_expiry`, expiryDate.toISOString());
  } catch (error) {
    console.warn('Failed to store consent settings:', error);
  }
};

export const isConsentExpired = (): boolean => {
  try {
    const expiry = localStorage.getItem(`${CONSENT_COOKIE}_expiry`);
    if (!expiry) return true;
    
    return new Date() > new Date(expiry);
  } catch {
    return true;
  }
};

export const updateGoogleAnalyticsConsent = (consent: ConsentSettings): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Update consent settings
    window.gtag('consent', 'update', {
      ...consent,
      // Disable additional tracking cookies that might be third-party
      ad_storage: 'denied', // Always deny ad storage to prevent third-party cookies
      ad_user_data: 'denied',
      ad_personalization: 'denied'
    });
    
    // Load GA script only after consent is granted
    if (consent.analytics_storage === 'granted' && !isGAScriptLoaded()) {
      loadGoogleAnalyticsScript();
    }
  }
};

let gaScriptLoaded = false;

export const isGAScriptLoaded = (): boolean => gaScriptLoaded;

export const loadGoogleAnalyticsScript = (): void => {
  if (gaScriptLoaded || typeof window === 'undefined') return;
  
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=G-0PC1CS0XCX';
  
  script.onload = () => {
    gaScriptLoaded = true;
    console.log('Google Analytics script loaded');
  };
  
  script.onerror = () => {
    console.warn('Failed to load Google Analytics script');
  };
  
  document.head.appendChild(script);
};

export const trackEvent = (eventName: string, parameters?: Record<string, any>): void => {
  const consent = getStoredConsent();
  
  if (consent?.analytics_storage === 'granted' && typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      ...parameters,
      // Ensure no third-party cookies are set
      send_page_view: false,
      anonymize_ip: true
    });
  }
};

export const trackPageView = (pagePath: string): void => {
  const consent = getStoredConsent();
  
  if (consent?.analytics_storage === 'granted' && typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-0PC1CS0XCX', {
      page_path: pagePath,
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      send_page_view: true
    });
  }
};

// Initialize consent on page load
export const initializeAnalyticsConsent = (): void => {
  const storedConsent = getStoredConsent();
  
  if (storedConsent && !isConsentExpired()) {
    updateGoogleAnalyticsConsent(storedConsent);
  }
  
  // Use pagehide instead of deprecated unload event
  if (typeof window !== 'undefined') {
    // Clean up any existing deprecated event listeners
    window.removeEventListener('unload', () => {});
  }
};

// Use page visibility API and pagehide event instead of deprecated unload event
export const setupPageVisibilityTracking = (): (() => void) | void => {
  if (typeof window !== 'undefined' && 'visibilityState' in document) {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Track page leave without using deprecated unload event
        trackEvent('page_leave', {
          page_path: window.location.pathname
        });
      }
    };

    const handlePageHide = () => {
      // Track page leave using pagehide event (replacement for deprecated unload)
      trackEvent('page_leave', {
        page_path: window.location.pathname
      });
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    
    // Cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }
};
